import {
  CAMERA_ENCODED_MAX_LENGTH,
  isValidCameraEncoded,
  MODEL_ID_REGEX,
  type ParsedShareUrl,
  type VariantUrlOverride,
  VARIANT_OVERRIDE_VALID,
  VIEWBOX_INDEX_MAX,
} from './types.js';

const EMBED_PATH_REGEX = /^\/embed\/([^/?#]+)\/?$/;

function readCameraParam(searchParams: URLSearchParams): string | null {
  const raw = searchParams.get('cam');
  if (raw === null) return null;
  if (raw.length === 0 || raw.length > CAMERA_ENCODED_MAX_LENGTH) return null;
  if (!isValidCameraEncoded(raw)) return null;
  return raw;
}

// Punto 2 SOG full SH 2026-06-08 (Codex iter1 HIGH #1 fix): readVariantParam
// strict whitelist (NON accetta string arbitrarie).
function readVariantParam(searchParams: URLSearchParams): VariantUrlOverride | null {
  const raw = searchParams.get('variant');
  if (raw === null) return null;
  return (VARIANT_OVERRIDE_VALID as readonly string[]).includes(raw)
    ? (raw as VariantUrlOverride)
    : null;
}

// readViewboxParam: integer 0..VIEWBOX_INDEX_MAX strict. Reject negativi, NaN,
// float, non-string. Defensive: scene_viewbox set possono avere indici
// non-contigui (UNIQUE constraint D1), cap massimo difensivo per evitare
// abuse query.
function readViewboxParam(searchParams: URLSearchParams): number | null {
  const raw = searchParams.get('vb');
  if (raw === null) return null;
  if (!/^\d+$/.test(raw)) return null;
  const n = Number.parseInt(raw, 10);
  if (!Number.isInteger(n) || n < 0 || n > VIEWBOX_INDEX_MAX) return null;
  return n;
}

export function parseShareUrl(href: string): ParsedShareUrl {
  let url: URL;
  try {
    url = new URL(href);
  } catch {
    return { kind: 'invalid', reason: 'unknown-route' };
  }

  const path = url.pathname.replace(/\/+$/, '') || '/';

  // /upload* → upload-fallback (anche /upload, /upload.html, /upload/foo)
  if (path === '/upload' || path.startsWith('/upload/') || path === '/upload.html') {
    return { kind: 'upload-fallback' };
  }

  // /embed/<id> → embed mode.
  // URL.pathname mantiene il percent-encoding (es. /embed/mdl%5F0123... non
  // decoded), quindi il segmento va decoded prima del match con MODEL_ID_REGEX.
  const embedMatch = EMBED_PATH_REGEX.exec(path);
  if (embedMatch) {
    const rawSegment = embedMatch[1] ?? '';
    let id: string;
    try {
      id = decodeURIComponent(rawSegment);
    } catch {
      return { kind: 'invalid', reason: 'malformed-id' };
    }
    if (!MODEL_ID_REGEX.test(id)) {
      return { kind: 'invalid', reason: 'malformed-id' };
    }
    return {
      kind: 'embed',
      modelId: id,
      cameraEncoded: readCameraParam(url.searchParams),
      variantOverride: readVariantParam(url.searchParams),
      viewboxIndex: readViewboxParam(url.searchParams),
    };
  }

  // / con ?id=... → main
  if (path === '/' || path === '/index.html') {
    const id = url.searchParams.get('id');
    if (id === null || id === '') {
      return { kind: 'invalid', reason: 'no-id' };
    }
    if (!MODEL_ID_REGEX.test(id)) {
      return { kind: 'invalid', reason: 'malformed-id' };
    }
    return {
      kind: 'main',
      modelId: id,
      cameraEncoded: readCameraParam(url.searchParams),
      variantOverride: readVariantParam(url.searchParams),
      viewboxIndex: readViewboxParam(url.searchParams),
    };
  }

  return { kind: 'invalid', reason: 'unknown-route' };
}
